import { isPdlMissingValue } from '@web-studio/shared-schemas';
import { EditableContent } from '../../edit/EditableContent';
import { pickBlockField } from '../../lib/localized';
import { parseHoursSchedule } from '../../lib/splitHoursLines';
import type { BlockRenderProps } from '../../types';
import { FooterLegalLinks } from './FooterLegalLinks';
import { FooterSocialLinks } from './FooterSocialLinks';

function pickFooterContactField(
  content: BlockRenderProps['block']['content'],
  key: string,
  locale: string,
): string {
  const value = pickBlockField(content, key, locale);
  return isPdlMissingValue(value) ? '' : value;
}

/** Compact utility dock — brand + social + tel/mail strip. */
export function FooterUtility({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
  pages,
  defaultLocale = locale,
  onNavigateToPageSlug,
}: BlockRenderProps) {
  const siteTitle = pickBlockField(block.content, 'siteTitle', locale);
  const address = pickFooterContactField(block.content, 'address', locale);
  const phone = pickFooterContactField(block.content, 'phone', locale);
  const email = pickFooterContactField(block.content, 'email', locale);
  const hours = pickFooterContactField(block.content, 'hours', locale);
  const hoursRows = parseHoursSchedule(hours);
  const legalLine = pickBlockField(block.content, 'legalLine', locale);
  const target = isActive ? activeEditTarget : null;

  return (
    <footer
      className={['ws-footer-utility', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-footer-arrangement="utility"
      id="contact"
    >
      <div className="ws-footer-utility__inner">
        <div className="ws-footer-utility__dock">
          <div className="ws-footer-utility__identity">
            <EditableContent
              as="p"
              fieldKey="siteTitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-footer-utility__brand"
            >
              {siteTitle}
            </EditableContent>
            <FooterSocialLinks
              content={block.content}
              locale={locale}
              editable={editable}
              activeEditTarget={target}
              className="ws-footer-utility__social"
            />
          </div>
          <div className="ws-footer-utility__actions">
            <EditableContent
              as="p"
              fieldKey="phone"
              editable={editable}
              activeEditTarget={target}
              className="ws-footer-utility__action"
            >
              {phone ? <a href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a> : null}
            </EditableContent>
            <EditableContent
              as="p"
              fieldKey="email"
              editable={editable}
              activeEditTarget={target}
              className="ws-footer-utility__action"
            >
              {email ? <a href={`mailto:${email}`}>{email}</a> : null}
            </EditableContent>
          </div>
        </div>
        <EditableContent
          as="p"
          fieldKey="address"
          editable={editable}
          activeEditTarget={target}
          className="ws-footer-utility__address"
        >
          {address}
        </EditableContent>
        {hoursRows.length > 0 ? (
          <EditableContent
            as="div"
            fieldKey="hours"
            editable={editable}
            activeEditTarget={target}
            className="ws-footer-utility__hours ws-hours-schedule"
          >
            {hoursRows.map((row, index) => (
              <div key={index} className="ws-hours-schedule__row">
                <span className="ws-hours-schedule__day">{row.day}</span>
                <span className="ws-hours-schedule__time">{row.time}</span>
              </div>
            ))}
          </EditableContent>
        ) : null}
        <div className="ws-footer-utility__legal">
          <FooterLegalLinks
            pages={pages}
            locale={locale}
            defaultLocale={defaultLocale}
            editable={editable}
            activeEditTarget={target}
            content={block.content}
            onNavigateToPageSlug={onNavigateToPageSlug}
          />
          <EditableContent
            fieldKey="legalLine"
            editable={editable}
            activeEditTarget={target}
            className="ws-footer__legal-line"
          >
            {legalLine}
          </EditableContent>
        </div>
      </div>
    </footer>
  );
}
